/**
 * Head of every project page: a dark card with title and meta line, optionally a stage below
 * them, and the tabs of the page as a floating pill on its lower edge.
 *
 *   ┌ Title ⌄                                 ┐  always
 *   │ Meta line                               │  always (if set)
 *   │ Stage (status bar, chart …)             │  only when the page has one
 *   └─[ Tab | Tab | Tab ]─────────────────────┘  only when the page has tabs
 *
 * Collapsed, the head has its minimum height: everything except the stage. The stage is as high
 * as its content, so the expanded head is as well. A page without a stage is always collapsed.
 *
 * The head stays at the top while scrolling and collapses once the page has scrolled down;
 * back at the top it expands again. Height changes are animated (`Collapse`), on scrolling as on
 * changing the page – the head lives in the layout and is not rebuilt with the page. Reduced
 * motion is covered by the global rule in index.css.
 *
 * Rules for the stage: read-only, at most one main action; collapsed, the action is gone with
 * the stage.
 */
import React, { useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Collapse, Tooltip, Typography } from "@mui/material";
import { WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import { colors, fonts } from "../../styles/theme";

export interface PageHeaderTab {
  key: string;
  label: string;
  active: boolean;
  onSelect: () => void;
  /** Warning triangle next to the label, like in the sidebar */
  warning?: boolean;
}

interface PageHeaderProps {
  title: string;
  /** Next to the title, e.g. the switcher between the instances of the page */
  titleAddon?: React.ReactNode;
  /** One line below the title; may carry a short form of the stage – it stays when collapsed */
  meta?: React.ReactNode;
  stage?: React.ReactNode;
  tabs?: PageHeaderTab[];
  /** Below the card on the page background, e.g. the way back from an opened record */
  below?: React.ReactNode;
}

/** Scrolled further than this (px), the head collapses … */
const COLLAPSE_AT = 64;
/** … and only expands again this close to the top – the gap keeps it from flickering */
const EXPAND_AT = 4;
/** Height of the tab pill that reaches below the card */
const TABS_OVERLAP = 24;
const TRANSITION_MS = 250;

/**
 * Collapsed once the page is scrolled down. It only collapses when the page is long enough to
 * stay scrolled afterwards – otherwise the shrinking head would pull it back to the top and the
 * head would expand again at once.
 */
const useCollapsedOnScroll = (stageRef: React.RefObject<HTMLElement | null>) => {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setCollapsed((previous) => {
        if (previous) return y > EXPAND_AT;
        const scrollable =
          document.documentElement.scrollHeight - window.innerHeight;
        const stageHeight = stageRef.current?.offsetHeight ?? 0;
        return y > COLLAPSE_AT && scrollable > stageHeight + COLLAPSE_AT;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [stageRef]);

  return collapsed;
};

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleAddon,
  meta,
  stage,
  tabs,
  below,
}) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const collapsed = useCollapsedOnScroll(stageRef);
  const hasTabs = !!tabs && tabs.length > 0;

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        // Covers the content scrolling underneath, around the rounded card as well
        backgroundColor: colors.background.default,
        pt: 1,
        mb: 3,
      }}
    >
      <Box
        component="header"
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 4,
          px: 3.5,
          pt: 2.5,
          pb: hasTabs ? `${TABS_OVERLAP + 16}px` : 2.5,
          color: colors.pageHeader.text,
          background: `linear-gradient(120deg, ${colors.pageHeader.gradientStart} 0%, ${colors.pageHeader.gradientMiddle} 50%, ${colors.pageHeader.gradientEnd} 100%)`,
          // Light spot in the upper right corner – the depth of the head
          "&::after": {
            content: '""',
            position: "absolute",
            right: -80,
            top: -120,
            width: 380,
            height: 380,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${colors.pageHeader.glow}, transparent 65%)`,
            pointerEvents: "none",
          },
          "& > *": { position: "relative", zIndex: 1 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          <Typography
            component="h1"
            variant="h5"
            title={title}
            sx={{
              m: 0,
              minWidth: 0,
              fontWeight: fonts.weights.semiBold,
              lineHeight: 1.3,
              color: colors.pageHeader.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          {titleAddon}
        </Box>
        {meta && (
          <Typography
            component="div"
            variant="body2"
            sx={{ mt: 0.5, color: colors.pageHeader.textSecondary }}
          >
            {meta}
          </Typography>
        )}
        <Collapse in={!!stage && !collapsed} timeout={TRANSITION_MS}>
          <Box ref={stageRef} sx={{ pt: 2.5 }}>
            {stage}
          </Box>
        </Collapse>
      </Box>

      {hasTabs && (
        <Box
          role="tablist"
          sx={{
            display: "inline-flex",
            gap: 0.5,
            p: 0.625,
            ml: 3.5,
            mt: `-${TABS_OVERLAP}px`,
            position: "relative",
            zIndex: 2,
            borderRadius: 3.5,
            backgroundColor: colors.pageHeader.tabsBackground,
            boxShadow: `0 8px 24px ${colors.pageHeader.tabsShadow}`,
          }}
        >
          {tabs.map((tab) => (
            <ButtonBase
              key={tab.key}
              role="tab"
              aria-selected={tab.active}
              onClick={tab.onSelect}
              sx={{
                gap: 0.75,
                px: 2.25,
                py: 1,
                borderRadius: 2.5,
                fontSize: "0.9rem",
                fontWeight: fonts.weights.medium,
                color: tab.active
                  ? colors.pageHeader.text
                  : colors.text.secondary,
                background: tab.active
                  ? `linear-gradient(120deg, ${colors.pageHeader.gradientEnd}, ${colors.pageHeader.accent})`
                  : "transparent",
                boxShadow: tab.active
                  ? `0 3px 10px ${colors.pageHeader.tabActiveShadow}`
                  : "none",
                transition: `background-color ${TRANSITION_MS}ms ease, color ${TRANSITION_MS}ms ease`,
                "&:hover": tab.active
                  ? undefined
                  : { backgroundColor: colors.background.tableHeader },
              }}
            >
              {tab.label}
              {tab.warning && (
                <Tooltip title="Es gibt Warnungen auf dieser Seite" arrow>
                  <WarningAmberIcon
                    sx={{ fontSize: "1rem", color: colors.state.highlight }}
                  />
                </Tooltip>
              )}
            </ButtonBase>
          ))}
        </Box>
      )}
      {below && <Box sx={{ mt: 2 }}>{below}</Box>}
    </Box>
  );
};

export default PageHeader;
